<template>
  <v-container
    fluid
    style="max-width: 1300px; padding: 32px 24px;">
    <div
      class="d-flex align-center mb-4"
      style="gap: 12px;">
      <div>
        <div class="text-h6 font-weight-medium">
          Factory Capacity
        </div>
        <div class="text-caption grey--text">
          Production load and forecast across all factories
        </div>
      </div>
      <v-spacer />
      <div
        class="d-flex align-center"
        style="gap: 8px">
        <v-btn-toggle
          :value="mode"
          mandatory
          dense
          @change="changeMode">
          <v-btn
            small
            value="tentative"
            class="text-none"
            :disabled="loading.products || loading.utilization || loading.summary">
            <v-icon
              left
              x-small>
              mdi-progress-clock
            </v-icon>
            สินค้าตัวอย่าง
          </v-btn>
          <v-btn
            small
            value="producing"
            class="text-none"
            :disabled="loading.products || loading.utilization || loading.summary">
            <v-icon
              left
              x-small>
              mdi-factory
            </v-icon>
            สินค้าสำหรับขาย
          </v-btn>
        </v-btn-toggle>
        <v-btn
          outlined
          small
          class="text-none"
          @click="settingsOpen = true">
          <v-icon
            left
            small>
            mdi-cog
          </v-icon>
          Factory Settings
        </v-btn>
      </div>
    </div>

    <v-card
      outlined
      class="pa-4 mb-4">
      <v-row
        dense
        align="center"
        class="mb-3">
        <v-col
          cols="12"
          md="4">
          <date-picker-form
            v-model="windowStart"
            type="month"
            label="Start month"
            outlined
            dense
            hide-details
            :max="windowEnd"
            :disabled="loading.products || loading.utilization || loading.summary"
            @input="fetchAll" />
        </v-col>
        <v-col
          cols="12"
          md="4">
          <date-picker-form
            v-model="windowEnd"
            type="month"
            label="End month"
            outlined
            dense
            hide-details
            :min="windowStart"
            :disabled="loading.products || loading.utilization || loading.summary"
            @input="fetchAll" />
        </v-col>
        <v-spacer />
        <v-col cols="auto">
          <v-btn
            outlined
            small
            class="text-none"
            :disabled="loading.products || loading.utilization || loading.summary"
            @click="resetWindow">
            <v-icon
              left
              small>
              mdi-calendar-today
            </v-icon>
            Today
          </v-btn>
        </v-col>
      </v-row>

      <v-divider class="mb-3" />

      <v-row
        dense
        align="start">
        <v-col
          cols="12"
          md="4">
          <div class="text-caption grey--text text--darken-1 font-weight-bold mb-1">
            FACTORY
          </div>
          <v-autocomplete
            v-model="selectedFactoryIds"
            :items="factoryList"
            item-text="name"
            item-value="id"
            label="All factories"
            multiple
            outlined
            dense
            hide-details
            small-chips
            deletable-chips
            clearable
            :menu-props="{ offsetY: true }"
            :disabled="loading.products || loading.utilization || loading.summary"
            @change="fetchAll" />
        </v-col>
        <v-col
          cols="12"
          md="8">
          <div class="text-caption grey--text text--darken-1 font-weight-bold mb-1">
            PRODUCTION TYPE
          </div>
          <div
            class="d-flex flex-wrap"
            style="gap: 8px;">
            <v-chip
              v-for="pt in productionTypeChips"
              :key="pt.value"
              :color="selectedProductionTypes.includes(pt.value) ? 'primary' : undefined"
              :outlined="!selectedProductionTypes.includes(pt.value)"
              style="cursor: pointer;"
              :disabled="loading.products || loading.utilization || loading.summary"
              @click="toggleProductionType(pt.value)">
              <v-icon
                left
                x-small>
                {{ pt.icon }}
              </v-icon>
              {{ pt.label }} ({{ pt.count }})
            </v-chip>
          </div>
        </v-col>
      </v-row>
    </v-card>

    <capacity-summary-bar
      :mode="mode"
      :summary="summary"
      :loading="loading.summary" />

    <utilization-heatmap
      :utilization="filteredUtilization"
      :window-start="windowStartObj"
      :window-end="windowEndObj"
      :mode.sync="mode"
      :loading="loading.utilization"
      :selected-factory-id="selectedFactoryId"
      @factory-click="onFactoryClick" />

    <product-table
      ref="productTable"
      :products="products"
      :mode="mode"
      :loading="loading.products"
      :selected-factory-id="selectedFactoryId"
      @clear-factory="selectedFactoryId = null" />

    <factory-settings-dialog
      v-model="settingsOpen"
      :factories="factoryList"
      @saved="onSettingsSaved" />

    <rebalance-dialog
      v-model="rebalanceOpen"
      :suggestion="rebalanceSuggestion"
      @saved="fetchRebalanceSuggestion" />

    <v-btn
      fab
      fixed
      bottom
      right
      color="orange darken-1"
      dark
      title="Rebalance suggestion"
      @click="rebalanceOpen = true">
      <v-icon>mdi-shuffle-variant</v-icon>
    </v-btn>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import FactoryProvider from '@/resources/FactoryProvider'
import FactoryCapacityProvider from '@/resources/FactoryCapacityProvider'
import CapacitySummaryBar from '../components/CapacitySummaryBar.vue'
import UtilizationHeatmap from '../components/UtilizationHeatmap.vue'
import ProductTable from '../components/ProductTable.vue'
import FactorySettingsDialog from '../components/FactorySettingsDialog.vue'
import RebalanceDialog from '../components/RebalanceDialog.vue'

const FactoryService = new FactoryProvider()
const CapacityService = new FactoryCapacityProvider()

const toYearMonth = (year, month) => `${year}-${String(month).padStart(2, '0')}`

export default {
  name: 'FactoryCapacity',
  components: {
    CapacitySummaryBar,
    UtilizationHeatmap,
    ProductTable,
    FactorySettingsDialog,
    RebalanceDialog
  },
  data () {
    const now = new Date()
    const endDate = new Date(now.getFullYear(), now.getMonth() + 11, 1)
    return {
      selectedFactoryIds: [],
      selectedProductionTypes: [],
      windowStart: toYearMonth(now.getFullYear(), now.getMonth() + 1),
      windowEnd: toYearMonth(endDate.getFullYear(), endDate.getMonth() + 1),
      mode: 'tentative',

      summary: null,
      utilization: [],
      products: [],
      rebalanceSuggestion: [],
      factoryList: [],

      selectedFactoryId: null,
      settingsOpen: false,
      rebalanceOpen: false,

      loading: {
        summary: false,
        utilization: false,
        products: false
      }
    }
  },
  computed: {
    windowStartObj () {
      const [y, m] = this.windowStart.split('-')
      return { year: +y, month: +m }
    },
    windowEndObj () {
      const [y, m] = this.windowEnd.split('-')
      return { year: +y, month: +m }
    },
    windowLabel () {
      const [sy, sm] = this.windowStart.split('-')
      const [ey, em] = this.windowEnd.split('-')
      const fmt = (y, m) => new Date(+y, +m - 1, 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
      return `${fmt(sy, sm)} – ${fmt(ey, em)}`
    },
    productionTypeChips () {
      const icons = {
        WOVEN: 'mdi-grid',
        JERSEY: 'mdi-tshirt-crew',
        ACTIVEWEAR: 'mdi-run',
        JEANS: 'mdi-hanger',
        KNIT: 'mdi-vector-combine',
        BAG: 'mdi-bag-personal',
        ACC: 'mdi-necklace',
        FOOTWEAR: 'mdi-shoe-sneaker',
        EYEWEAR: 'mdi-glasses'
      }
      const options = [
        { value: 'WOVEN', label: 'Woven' },
        { value: 'JERSEY', label: 'Jersey' },
        { value: 'ACTIVEWEAR', label: 'Activewear' },
        { value: 'JEANS', label: 'Jeans' },
        { value: 'KNIT', label: 'Knit' },
        { value: 'BAG', label: 'Bag' },
        { value: 'ACC', label: 'Acc' },
        { value: 'FOOTWEAR', label: 'Footwear' },
        { value: 'EYEWEAR', label: 'Eyewear' }
      ]
      return options.map((pt) => ({
        ...pt,
        icon: icons[pt.value] || 'mdi-tag',
        count: this.factoryList.filter((f) => f.productionTypes && f.productionTypes.includes(pt.value)).length
      }))
    },
    filteredUtilization () {
      let result = this.utilization
      if (this.selectedFactoryIds.length) {
        result = result.filter((f) => this.selectedFactoryIds.includes(f.factoryId))
      }
      if (this.selectedProductionTypes.length) {
        const ids = this.factoryList
          .filter((f) => f.productionTypes && f.productionTypes.some((t) => this.selectedProductionTypes.includes(t)))
          .map((f) => f.id)
        result = result.filter((f) => ids.includes(f.factoryId))
      }
      return result
    }
  },
  watch: {
    mode () {
      this.fetchProducts()
    },
    rebalanceOpen (val) {
      if (val) this.fetchRebalanceSuggestion()
    }
  },
  mounted () {
    this.loadFactoryList()
    this.fetchAll()
  },
  methods: {
    ...mapActions({
      setSnackbar: 'Components/setSnackbar'
    }),
    changeMode (value) {
      this.mode = value
    },
    async loadFactoryList () {
      try {
        const { data } = await FactoryService.getAll()
        this.factoryList = data.results || []
      } catch (e) {
        console.error('loadFactoryList', e?.message || e)
      }
    },
    async fetchAll () {
      await Promise.all([
        this.fetchSummary(),
        this.fetchUtilization(),
        this.fetchProducts(),
        this.fetchRebalanceSuggestion()
      ])
    },
    async fetchSummary () {
      this.loading.summary = true
      try {
        const { data } = await CapacityService.getSummary({
          factoryIds: this.selectedFactoryIds,
          productionTypes: this.selectedProductionTypes,
          start: this.windowStart,
          end: this.windowEnd
        })
        this.summary = data
      } catch (e) {
        console.error('fetchSummary', e?.message || e)
        this.setSnackbar({ value: true, message: `Failed to load summary: ${e?.message || e}`, type: 'error' })
      } finally {
        this.loading.summary = false
      }
    },
    async fetchUtilization () {
      this.loading.utilization = true
      try {
        const { data } = await CapacityService.getUtilization({
          factoryIds: this.selectedFactoryIds,
          productionTypes: this.selectedProductionTypes,
          start: this.windowStart,
          end: this.windowEnd
        })
        this.utilization = data
      } catch (e) {
        console.error('fetchUtilization', e?.message || e)
        this.setSnackbar({ value: true, message: `Failed to load utilization: ${e?.message || e}`, type: 'error' })
      } finally {
        this.loading.utilization = false
      }
    },
    async fetchProducts () {
      this.loading.products = true
      try {
        const { data } = await CapacityService.getProducts({
          factoryIds: this.selectedFactoryIds,
          productionTypes: this.selectedProductionTypes,
          start: this.windowStart,
          end: this.windowEnd,
          mode: this.mode
        })
        this.products = data.results || data
      } catch (e) {
        console.error('fetchProducts', e?.message || e)
        this.setSnackbar({ value: true, message: `Failed to load products: ${e?.message || e}`, type: 'error' })
      } finally {
        this.loading.products = false
      }
    },
    async fetchRebalanceSuggestion () {
      try {
        const { data } = await CapacityService.getRebalanceSuggestion()
        this.rebalanceSuggestion = data
      } catch (e) {
        console.error('fetchRebalanceSuggestion', e?.message || e)
      }
    },
    resetWindow () {
      const now = new Date()
      const endDate = new Date(now.getFullYear(), now.getMonth() + 11, 1)
      this.windowStart = toYearMonth(now.getFullYear(), now.getMonth() + 1)
      this.windowEnd = toYearMonth(endDate.getFullYear(), endDate.getMonth() + 1)
      this.fetchAll()
    },
    toggleProductionType (value) {
      const idx = this.selectedProductionTypes.indexOf(value)
      if (idx >= 0) {
        this.selectedProductionTypes.splice(idx, 1)
      } else {
        this.selectedProductionTypes.push(value)
      }
      this.fetchAll()
    },
    onFactoryClick (factoryId) {
      this.selectedFactoryId = this.selectedFactoryId === factoryId ? null : factoryId
      if (this.selectedFactoryId !== null) {
        this.$nextTick(() => {
          this.$refs.productTable.$el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        })
      }
    },
    async onSettingsSaved () {
      this.settingsOpen = false
      await Promise.all([this.loadFactoryList(), this.fetchSummary(), this.fetchUtilization()])
    }
  }
}
</script>
